import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Search, Download, LogOut, Settings as SettingsIcon, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { id: "", label: "All" },
  { id: "seller", label: "Sellers" },
  { id: "investor", label: "Investors" },
  { id: "buyer", label: "Buyers" },
  { id: "partner", label: "Partners" },
  { id: "contact", label: "Contact" },
];
const STATUSES = ["new", "contacted", "appointment_set", "offer_made", "under_contract", "follow_up_later", "dead"];
const PRIORITIES = ["Hot", "Warm", "Cold"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!user);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [q, setQ] = useState("");
  const [notif, setNotif] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (user) return;
    api.get("/auth/me").then((r) => {
      if (!r.data.is_admin) navigate("/admin?error=unauthorized");
      else { setUser(r.data); setAuthChecked(true); }
    }).catch(() => navigate("/admin"));
  }, [navigate, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (type) params.lead_type = type;
      if (status) params.status = status;
      if (q) params.q = q;
      if (notif) params.notification = notif;
      const r = await api.get("/admin/leads", { params });
      setItems(r.data || []);
    } catch (e) { toast.error("Failed to load leads"); }
    setLoading(false);
  }, [type, status, q, notif]);

  useEffect(() => { if (authChecked || user) load(); }, [authChecked, user, load]);

  const retryFailed = async () => {
    if (!window.confirm("Re-send the owner notification email for every saved lead that never got one? This sends real emails to the team inboxes.")) return;
    setRetrying(true);
    try {
      const r = await api.post("/admin/notifications/retry-failed");
      toast.success(`Retried ${r.data.attempted}, SendGrid accepted ${r.data.sent}`);
      load();
    } catch (e) { toast.error("Retry failed"); }
    setRetrying(false);
  };

  const logout = async () => { await api.post("/auth/logout"); navigate("/admin"); };

  const exportCsv = async () => {
    const res = await api.get("/admin/leads-export", { params: { ...(type ? { lead_type: type } : {}), format: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url;
    a.download = `bscv-leads-${type || "all"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportJson = async () => {
    const res = await api.get("/admin/leads-export", { params: { ...(type ? { lead_type: type } : {}), format: "json" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url;
    a.download = `bscv-leads-${type || "all"}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="min-h-screen bg-black text-neutral-400 flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-black text-neutral-200" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/85 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-display text-base">Brightside · Admin</Link>
            <span className="hidden md:inline text-xs text-neutral-500">{user.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/case-studies" className="text-sm text-neutral-300 hover:text-[#C19B6C] inline-flex items-center gap-2"><FileText size={14}/> Case Studies</Link>
            <Link to="/admin/settings" className="text-sm text-neutral-300 hover:text-[#C19B6C] inline-flex items-center gap-2"><SettingsIcon size={14}/> Settings</Link>
            <button onClick={logout} className="text-sm text-neutral-300 hover:text-[#C19B6C] inline-flex items-center gap-2" data-testid="admin-logout"><LogOut size={14}/> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-10">
        <h1 className="font-display text-3xl">Leads</h1>

        {/* Quick filter chips */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="quick-filters">
          {[
            { label: "All new", set: () => { setStatus("new"); setPriority(""); setType(""); } },
            { label: "Hot", set: () => { setPriority("Hot"); setStatus(""); } },
            { label: "Follow-up", set: () => { setStatus("follow_up_later"); setPriority(""); } },
            { label: "Sellers", set: () => { setType("seller"); setStatus(""); setPriority(""); } },
            { label: "Reset", set: () => { setStatus(""); setPriority(""); setType(""); setQ(""); } },
          ].map((c) => (
            <button key={c.label} onClick={c.set} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-white/10 hover:border-[#C19B6C]/60 text-neutral-300 transition-colors" data-testid={`quick-${c.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`tab-${t.id || 'all'}`} className={`px-4 py-2 text-xs uppercase tracking-wider border ${type === t.id ? "border-[#C19B6C] text-[#C19B6C]" : "border-white/15 text-neutral-300 hover:border-white/40"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, address" className="w-full bg-[#0F0F0F] border border-white/10 pl-9 pr-3 py-2.5 text-sm" data-testid="admin-search" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm" data-testid="admin-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm" data-testid="admin-priority-filter">
            <option value="">All priorities</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={notif} onChange={(e) => setNotif(e.target.value)} className="bg-[#0F0F0F] border border-white/10 px-3 py-2.5 text-sm" data-testid="admin-notif-filter">
            <option value="">All notifications</option>
            <option value="failed">Owner email not sent</option>
            <option value="sent">Owner email sent</option>
          </select>
          <button onClick={load} className="btn-ghost px-4 py-2.5 text-sm" data-testid="admin-refresh">Refresh</button>
          <button onClick={retryFailed} disabled={retrying} className="btn-ghost px-4 py-2.5 text-sm disabled:opacity-50" data-testid="admin-retry-failed">{retrying ? "Retrying…" : "Retry failed emails"}</button>
          <button onClick={exportCsv} className="btn-gold px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="admin-export"><Download size={14} /> CSV</button>
          <button onClick={exportJson} className="btn-ghost px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="admin-export-json"><Download size={14} /> JSON</button>
        </div>

        <div className="mt-8 card-frame overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-500">
              <tr className="border-b border-white/10">
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Priority</th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-left p-4">Detail</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
                <th className="text-left p-4">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="p-8 text-center text-neutral-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-neutral-500">No leads found.</td></tr>
              ) : items.map((l) => {
                const pri = l.lead_priority || "Cold";
                const priCls = pri === "Hot" ? "bg-red-500/15 text-red-300 border-red-500/30"
                  : pri === "Warm" ? "bg-[#C19B6C]/15 text-[#C19B6C] border-[#C19B6C]/30"
                  : "bg-white/5 text-neutral-400 border-white/10";
                return (
                <tr key={l.id} className={`border-b border-white/5 hover:bg-white/[0.02] cursor-pointer ${pri === "Hot" ? "bg-red-500/[0.04]" : ""}`} style={{ borderLeft: `4px solid ${pri === "Hot" ? "#ef4444" : pri === "Warm" ? "#C19B6C" : "transparent"}` }} onClick={() => navigate(`/admin/leads/${l.id}`)} data-testid={`row-${l.id}`}>
                  <td className="p-4 text-[#C19B6C] uppercase text-xs tracking-wider">
                    {l.lead_type}
                    {l.duplicate_flag && <div className="text-[10px] text-amber-400 mt-1 normal-case tracking-normal">duplicate</div>}
                    {l.email_sent !== true && <div className="text-[10px] text-red-400 mt-1 normal-case tracking-normal" data-testid={`row-notif-failed-${l.id}`}>email not sent</div>}
                  </td>
                  <td className="p-4"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${priCls}`}>{pri}</span></td>
                  <td className="p-4">{l.name}</td>
                  <td className="p-4 text-neutral-400">{l.email}<br /><span className="text-xs">{l.phone}</span></td>
                  <td className="p-4 text-neutral-400 text-xs max-w-xs truncate">{l.property_address || l.capital_range || l.buyer_type || l.message || "—"}</td>
                  <td className="p-4 text-xs uppercase">{l.status}</td>
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="text-[#C19B6C] hover:text-white text-xs underline" data-testid={`row-call-${l.id}`}>Call</a>
                      )}
                      <button onClick={async () => { try { await api.patch(`/admin/leads/${l.id}`, { status: "contacted" }); load(); } catch {} }} className="text-neutral-300 hover:text-[#C19B6C] text-xs" data-testid={`row-contacted-${l.id}`}>Contacted</button>
                    </div>
                  </td>
                  <td className="p-4 text-xs text-neutral-500">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
